import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import {
  Question, MagnifyingGlass, X, ArrowLeft, ThumbsUp, ThumbsDown, Sparkle,
  Book, ArrowRight, ChatCircleText, CursorClick,
} from "@phosphor-icons/react";
import API from "../lib/api";
import { KbMarkdown, KbThumb } from "./kbMarkdown";
import { useAuth } from "../contexts/AuthContext";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { KbWalkthrough } from "./KbWalkthrough";
import { useLaunchLiveTour, TOUR_ROLES } from "./LiveTour";

/**
 * Phase 287 — Floating "?" help button + slide-in drawer.
 *
 * - Always rendered (lives in Layout) for authenticated users.
 * - On open, fetches articles related to the current page via /api/kb/by-page.
 * - "Search" tab queries /api/kb/articles?q=
 * - "Ask AI" tab calls /api/kb/ask
 * - Click an article → inline detail view with thumbs up/down feedback.
 *
 * To embed in a portal, pass `portal` prop ("supplier" or "client") to lock the
 * role bucket. In the main app it defaults to the user's role.
 */

function publicRoleFor(user) {
  const r = user?.role || "";
  if (r === "in_house") return "in_house";
  if (r === "vendor") return "vendor";
  if (r === "client") return "client";
  return "admin";
}

export default function KbHelpDrawer({ portal = null, hideFab = false, defaultOpen = false }) {
  const { user } = useAuth();
  const location = useLocation();
  const launchLive = useLaunchLiveTour();
  const canTour = TOUR_ROLES.includes(user?.role) && !user?.impersonating;
  const [open, setOpen] = useState(defaultOpen);
  const [tab, setTab] = useState("page"); // page | search | ask
  const [pageArticles, setPageArticles] = useState([]);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [askQ, setAskQ] = useState("");
  const [askLoading, setAskLoading] = useState(false);
  const [askAnswer, setAskAnswer] = useState(null);
  const [activeSlug, setActiveSlug] = useState(null);
  const [activeArticle, setActiveArticle] = useState(null);
  const [voted, setVoted] = useState(null);

  const role = portal === "supplier" ? "vendor" : portal === "client" ? "client" : publicRoleFor(user);
  const isPublic = portal === "client" && !user; // unauthenticated client portal

  // Load related-to-current-page articles
  const loadPageArticles = useCallback(async () => {
    if (isPublic) {
      // For unauthenticated client portals, fall back to public listing
      try {
        const { data } = await API.get(`/public/kb/articles?role=client`);
        const flat = (data.sections || []).flatMap(s => s.articles || []).slice(0, 8);
        setPageArticles(flat);
      } catch {
        setPageArticles([]);
      }
      return;
    }
    try {
      const page = location.pathname.replace(/^\/+/, "");
      const { data } = await API.get(`/kb/by-page?page=${encodeURIComponent(page)}`);
      setPageArticles(data.articles || []);
    } catch {
      setPageArticles([]);
    }
  }, [location.pathname, isPublic]);

  // Reload when drawer opens or route changes
  useEffect(() => {
    if (open && tab === "page") loadPageArticles();
  }, [open, tab, loadPageArticles]);

  // Debounced search
  useEffect(() => {
    if (tab !== "search") return;
    if (!searchQ || searchQ.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const url = isPublic
          ? `/public/kb/articles?role=client&q=${encodeURIComponent(searchQ)}`
          : `/kb/articles?role=${role}&q=${encodeURIComponent(searchQ)}`;
        const { data } = await API.get(url);
        const flat = (data.sections || []).flatMap(s => (s.articles || []).map(a => ({ ...a, section_title: s.title })));
        setSearchResults(flat);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [searchQ, tab, role, isPublic]);

  // Open an article inline
  const openArticle = async (slug) => {
    setActiveSlug(slug);
    setActiveArticle(null);
    setVoted(null);
    try {
      const url = isPublic ? `/public/kb/articles/${slug}` : `/kb/articles/${slug}`;
      const { data } = await API.get(url);
      setActiveArticle(data);
    } catch {
      toast.error("Couldn't load article");
      setActiveSlug(null);
    }
  };

  const closeArticle = () => {
    setActiveSlug(null);
    setActiveArticle(null);
    setVoted(null);
  };

  const vote = async (helpful) => {
    if (voted || isPublic) {
      // Unauthenticated client portal can't submit feedback
      if (isPublic) toast.info("Sign in to share feedback");
      return;
    }
    setVoted(helpful ? "yes" : "no");
    try {
      await API.post(`/kb/articles/${activeSlug}/feedback`, { helpful });
      toast.success("Thanks for the feedback!");
    } catch {
      toast.error("Couldn't record feedback");
    }
  };

  const askAi = async () => {
    if (!askQ || askQ.trim().length < 3 || isPublic) {
      if (isPublic) toast.info("AI Q&A requires login");
      return;
    }
    setAskLoading(true);
    setAskAnswer(null);
    try {
      const { data } = await API.post(`/kb/ask`, { question: askQ.trim(), role });
      setAskAnswer(data);
    } catch (err) {
      const detail = err?.response?.data?.detail || "AI Q&A failed";
      toast.error(detail);
    } finally {
      setAskLoading(false);
    }
  };

  // Keyboard: Escape closes the drawer / back out of an article
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (activeSlug) closeArticle();
        else setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeSlug]);

  return (
    <>
      {!hideFab && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Help"
          title="Help"
          className="fixed bottom-24 md:bottom-6 right-4 md:right-6 z-40 h-11 w-11 rounded-full bg-primary text-primary-foreground shadow-lg hover:scale-105 hover:shadow-xl transition-all flex items-center justify-center group"
          data-testid="kb-help-fab"
        >
          <Question size={20} weight="bold" />
          <span className="absolute right-full mr-2 px-2 py-1 rounded bg-foreground/90 text-background text-[10px] font-medium opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
            Help
          </span>
        </button>
      )}

      {/* Drawer */}
      {open && (
        <div className="fixed inset-0 z-[70]" data-testid="kb-help-drawer">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
            onClick={() => setOpen(false)}
          />
          <div className="absolute right-0 top-0 h-full w-full sm:w-[420px] bg-card border-l border-border shadow-2xl flex flex-col animate-in slide-in-from-right duration-200">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
              <div className="flex items-center gap-2">
                <div className="h-7 w-7 rounded-md bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
                  <Book size={14} className="text-white" weight="fill" />
                </div>
                <div>
                  <h3 className="text-sm font-bold font-[Chivo]">Help & Knowledge</h3>
                  <p className="text-[10px] text-muted-foreground">Articles for your role</p>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="h-7 w-7 flex items-center justify-center rounded-md hover:bg-muted transition-colors"
                aria-label="Close"
                data-testid="kb-help-close"
              >
                <X size={16} />
              </button>
            </div>

            {/* Tab nav (hidden when reading an article) */}
            {!activeSlug && (
              <div className="flex border-b border-border shrink-0">
                <TabButton id="page" current={tab} onClick={setTab} label="This page" testid="kb-tab-page" />
                <TabButton id="search" current={tab} onClick={setTab} label="Search" testid="kb-tab-search" />
                {!isPublic && (
                  <TabButton id="ask" current={tab} onClick={setTab} label={<><Sparkle size={11} className="inline mr-0.5" weight="fill" />Ask AI</>} testid="kb-tab-ask" />
                )}
              </div>
            )}

            {/* Body */}
            <div className="flex-1 overflow-y-auto">
              {activeSlug ? (
                <ArticleView article={activeArticle} onBack={closeArticle} onVote={vote} voted={voted} hideFeedback={isPublic} onOpenArticle={openArticle} />
              ) : (
                <>
                  {tab === "page" && (
                    <>
                      {canTour && (
                        <button type="button" onClick={() => { setOpen(false); launchLive("dashboard"); }} className="w-full flex items-center gap-3 px-4 py-3 border-b border-border bg-primary/5 hover:bg-primary/10 text-left transition-colors" data-testid="kb-live-tour-btn">
                          <span className="h-8 w-8 rounded-full bg-primary text-primary-foreground grid place-items-center shrink-0"><CursorClick size={14} weight="fill" /></span>
                          <span className="min-w-0">
                            <span className="block text-sm font-semibold">Take the live dashboard tour</span>
                            <span className="block text-[11px] text-muted-foreground">Two minutes · highlights on your own screen, narrated</span>
                          </span>
                        </button>
                      )}
                      <PageArticlesPanel articles={pageArticles} onOpen={openArticle} />
                    </>
                  )}
                  {tab === "search" && (
                    <SearchPanel
                      q={searchQ}
                      onChange={setSearchQ}
                      results={searchResults}
                      searching={searching}
                      onOpen={openArticle}
                    />
                  )}
                  {tab === "ask" && !isPublic && (
                    <AskPanel
                      q={askQ}
                      onChange={setAskQ}
                      onAsk={askAi}
                      loading={askLoading}
                      answer={askAnswer}
                      onOpen={openArticle}
                    />
                  )}
                </>
              )}
            </div>

            {/* Footer hint */}
            <div className="px-4 py-2 border-t border-border text-[10px] text-muted-foreground shrink-0 flex items-center justify-between">
              <span>Role: <strong className="capitalize">{role}</strong></span>
              {!isPublic && (
                <a href="/knowledge-base" className="text-primary hover:underline flex items-center gap-1">
                  Full Knowledge Base <ArrowRight size={10} />
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function TabButton({ id, current, onClick, label, testid }) {
  const active = current === id;
  return (
    <button
      type="button"
      onClick={() => onClick(id)}
      className={`flex-1 px-3 py-2 text-xs font-medium border-b-2 transition-colors ${active ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
      data-testid={testid}
    >
      {label}
    </button>
  );
}

function PageArticlesPanel({ articles, onOpen }) {
  if (!articles || articles.length === 0) {
    return (
      <div className="text-center py-10 px-6">
        <Book size={28} className="mx-auto text-muted-foreground/40 mb-2" />
        <p className="text-xs text-muted-foreground">No page-specific help yet. Try the Search or Ask AI tabs.</p>
      </div>
    );
  }
  return (
    <div className="divide-y divide-border" data-testid="kb-page-articles">
      {articles.map(a => (
        <ArticleRow key={a.slug} article={a} onOpen={onOpen} />
      ))}
    </div>
  );
}

function SearchPanel({ q, onChange, results, searching, onOpen }) {
  return (
    <div>
      <div className="p-3 border-b border-border sticky top-0 bg-card z-10">
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={e => onChange(e.target.value)}
            placeholder="Search articles..."
            className="pl-8 h-9 text-sm"
            data-testid="kb-help-search-input"
            autoFocus
          />
        </div>
      </div>
      {searching ? (
        <p className="text-center text-xs text-muted-foreground py-8">Searching…</p>
      ) : q.length < 2 ? (
        <p className="text-center text-xs text-muted-foreground py-8">Type at least 2 characters</p>
      ) : results.length === 0 ? (
        <p className="text-center text-xs text-muted-foreground py-8">No results for "{q}"</p>
      ) : (
        <div className="divide-y divide-border" data-testid="kb-help-search-results">
          {results.map(a => (
            <ArticleRow key={a.slug} article={a} onOpen={onOpen} />
          ))}
        </div>
      )}
    </div>
  );
}

function AskPanel({ q, onChange, onAsk, loading, answer, onOpen }) {
  const [askVote, setAskVote] = useState(null); // 'yes' | 'no' | null
  const [askComment, setAskComment] = useState("");
  const [showCommentBox, setShowCommentBox] = useState(false);
  const [submittingFeedback, setSubmittingFeedback] = useState(false);

  // Reset feedback state whenever a new answer arrives
  useEffect(() => {
    setAskVote(null);
    setAskComment("");
    setShowCommentBox(false);
  }, [answer]);

  const submitAskFeedback = async (helpful) => {
    if (!answer?.ask_id || askVote) return;
    if (!helpful && !showCommentBox) {
      // First click on 👎 reveals the comment box, doesn't submit yet
      setShowCommentBox(true);
      return;
    }
    setSubmittingFeedback(true);
    try {
      await API.post(`/kb/ask/${answer.ask_id}/feedback`, {
        helpful,
        comment: helpful ? "" : askComment.trim(),
      });
      setAskVote(helpful ? "yes" : "no");
      if (helpful) toast.success("Thanks for the feedback!");
      else toast.success("Logged as a knowledge gap. Admins will improve this.");
    } catch {
      toast.error("Couldn't record feedback");
    } finally {
      setSubmittingFeedback(false);
    }
  };

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
        <Sparkle size={11} weight="fill" className="text-indigo-500" />
        Ask in plain English. Answers are grounded in our KB.
      </div>
      <div>
        <textarea
          value={q}
          onChange={e => onChange(e.target.value)}
          placeholder="e.g. How do I schedule a multi-day job?"
          rows={3}
          className="w-full text-sm rounded-md border border-border bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
          data-testid="kb-help-ask-input"
          maxLength={500}
        />
        <Button
          onClick={onAsk}
          disabled={loading || q.trim().length < 3}
          size="sm"
          className="w-full mt-2"
          data-testid="kb-help-ask-submit"
        >
          {loading ? "Thinking…" : (<><ChatCircleText size={14} className="mr-1" />Ask</>)}
        </Button>
      </div>
      {answer && (
        <div className="border border-border rounded-md p-3 bg-muted/30" data-testid="kb-help-ask-answer">
          <p className="text-xs whitespace-pre-wrap leading-relaxed">{answer.answer}</p>
          {answer.citations && answer.citations.length > 0 && (
            <div className="mt-3 pt-2 border-t border-border">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">Sources</p>
              <div className="flex flex-col gap-1">
                {answer.citations.map((c, i) => (
                  <button
                    key={c.slug}
                    onClick={() => onOpen(c.slug)}
                    className="text-[11px] text-primary hover:underline text-left"
                    data-testid={`kb-help-citation-${i}`}
                  >
                    [{i + 1}] {c.title}
                  </button>
                ))}
              </div>
            </div>
          )}
          {/* Phase 287.1 — AI answer feedback → knowledge gap loop */}
          {answer.ask_id && (
            <div className="mt-3 pt-2 border-t border-border" data-testid="kb-help-ask-feedback">
              {askVote ? (
                <p className="text-[10px] text-muted-foreground italic">
                  {askVote === "yes" ? "Thanks for confirming this helped." : "Reported — we'll improve this."}
                </p>
              ) : (
                <>
                  <p className="text-[10px] text-muted-foreground mb-1.5">Did this answer help?</p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => submitAskFeedback(true)}
                      disabled={submittingFeedback}
                      className="flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-border hover:bg-muted transition-colors"
                      data-testid="kb-ask-vote-yes"
                    >
                      <ThumbsUp size={11} /> Yes
                    </button>
                    <button
                      type="button"
                      onClick={() => submitAskFeedback(false)}
                      disabled={submittingFeedback}
                      className="flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-border hover:bg-muted transition-colors"
                      data-testid="kb-ask-vote-no"
                    >
                      <ThumbsDown size={11} /> No
                    </button>
                  </div>
                  {showCommentBox && (
                    <div className="mt-2 space-y-1.5" data-testid="kb-ask-comment-box">
                      <textarea
                        value={askComment}
                        onChange={e => setAskComment(e.target.value.slice(0, 500))}
                        placeholder="Optional — what were you actually looking for?"
                        rows={2}
                        className="w-full text-xs rounded border border-border bg-background px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary"
                        data-testid="kb-ask-comment-input"
                      />
                      <button
                        type="button"
                        onClick={() => submitAskFeedback(false)}
                        disabled={submittingFeedback}
                        className="text-[11px] px-2 py-1 rounded bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                        data-testid="kb-ask-submit-gap"
                      >
                        Send feedback
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ArticleRow({ article, onOpen }) {
  return (
    <button
      type="button"
      onClick={() => onOpen(article.slug)}
      className="w-full flex items-start gap-2 px-4 py-3 hover:bg-muted/50 text-left transition-colors"
      data-testid={`kb-help-row-${article.slug}`}
    >
      <KbThumb src={article.thumbnail} size="sm" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium leading-tight">{article.title}</p>
        {article.section_title && (
          <p className="text-[10px] text-muted-foreground mt-0.5">{article.section_title}</p>
        )}
        {article.summary && (
          <p className="text-[11px] text-muted-foreground line-clamp-2 mt-1">{article.summary}</p>
        )}
      </div>
      {(article.helpful_count || 0) > 0 && (
        <span className="text-[10px] text-emerald-600 dark:text-emerald-400 shrink-0 mt-1">👍 {article.helpful_count}</span>
      )}
    </button>
  );
}

function ArticleView({ article, onBack, onVote, voted, hideFeedback, onOpenArticle }) {
  if (!article) {
    return <p className="text-center text-xs text-muted-foreground py-12">Loading…</p>;
  }
  return (
    <div className="p-4" data-testid="kb-help-article-view">
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground mb-3"
        data-testid="kb-help-article-back"
      >
        <ArrowLeft size={12} /> Back
      </button>
      <h3 className="text-sm font-bold font-[Chivo] mb-1">{article.title}</h3>
      {article.summary && (
        <p className="text-xs text-muted-foreground italic mb-3">{article.summary}</p>
      )}
      <KbWalkthrough walkthrough={article.walkthrough} slug={article.slug} compact />
      <div className="text-sm leading-relaxed space-y-1">
        <KbMarkdown content={article.content} compact onOpenArticle={onOpenArticle} />
      </div>
      {!hideFeedback && (
        <div className="mt-4 pt-3 border-t border-border" data-testid="kb-help-article-feedback">
          <p className="text-[10px] text-muted-foreground mb-1.5">Was this helpful?</p>
          <div className="flex gap-2">
            <Button
              variant={voted === "yes" ? "default" : "outline"}
              size="sm"
              onClick={() => onVote(true)}
              disabled={!!voted}
              data-testid="kb-help-vote-yes"
            >
              <ThumbsUp size={12} className="mr-1" /> Yes
            </Button>
            <Button
              variant={voted === "no" ? "destructive" : "outline"}
              size="sm"
              onClick={() => onVote(false)}
              disabled={!!voted}
              data-testid="kb-help-vote-no"
            >
              <ThumbsDown size={12} className="mr-1" /> No
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
